import React from "react";
import { STATUS_LABELS } from "../../i18n";
import { useI18n } from "../../i18n";

const STATUS_STYLE = {
  new: "bg-slate-100 text-slate-700",
  awaiting_documents: "bg-amber-50 text-amber-700",
  documents_received: "bg-sky-50 text-sky-700",
  ai_review: "bg-violet-50 text-violet-700",
  specialist_review: "bg-blue-50 text-blue-700",
  additional_info_required: "bg-amber-50 text-amber-700",
  supervisor_review: "bg-indigo-50 text-indigo-700",
  quality_review: "bg-fuchsia-50 text-fuchsia-700",
  client_approval: "bg-emerald-50 text-emerald-700",
  completed: "bg-emerald-100 text-emerald-800",
  closed: "bg-slate-100 text-slate-500",
};

const RISK_STYLE = {
  low: "bg-emerald-50 text-emerald-700 border-emerald-200",
  moderate: "bg-amber-50 text-amber-700 border-amber-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  high: "bg-orange-50 text-orange-700 border-orange-200",
  critical: "bg-red-50 text-red-700 border-red-200",
};
const RISK_LABEL = {
  low: { en: "Low", ar: "منخفض" }, moderate: { en: "Moderate", ar: "متوسط" }, medium: { en: "Medium", ar: "متوسط" },
  high: { en: "High", ar: "مرتفع" }, critical: { en: "Critical", ar: "حرج" },
};
const CONF_LABEL = { high: { en: "High", ar: "عالية" }, medium: { en: "Medium", ar: "متوسطة" }, low: { en: "Low", ar: "منخفضة" } };

export function StatusBadge({ status }) {
  const { lang } = useI18n();
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLE[status] || "bg-slate-100 text-slate-600"}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {STATUS_LABELS[status] ? STATUS_LABELS[status][lang] : status}
    </span>
  );
}

export function RiskPill({ level }) {
  const { lang } = useI18n();
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${RISK_STYLE[level] || RISK_STYLE.moderate}`}>
      {(RISK_LABEL[level] || RISK_LABEL.moderate)[lang]}
    </span>
  );
}

export function ConfidenceBadge({ level }) {
  const { lang, t } = useI18n();
  const color = level === "high" ? "text-emerald-600" : level === "low" ? "text-red-600" : "text-amber-600";
  return <span className={`text-xs font-medium ${color}`}>{t("confidence")}: {(CONF_LABEL[level] || CONF_LABEL.medium)[lang]}</span>;
}

const PIPE = ["new", "documents_received", "ai_review", "specialist_review", "supervisor_review", "quality_review", "client_approval", "completed"];

export function WorkflowPipeline({ status }) {
  const { lang } = useI18n();
  const idx = PIPE.indexOf(status);
  const activeIdx = idx === -1 ? (["awaiting_documents"].includes(status) ? 0 : status === "additional_info_required" ? 3 : 0) : idx;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="case-status-pipeline">
      {PIPE.map((s, i) => (
        <div key={s} className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium ${i <= activeIdx ? "bg-[hsl(217_63%_15%)] text-white" : "bg-secondary text-muted-foreground"}`}>
          {STATUS_LABELS[s][lang]}
        </div>
      ))}
    </div>
  );
}

export function HealthGauge({ score, size = 160 }) {
  const { t } = useI18n();
  const r = (size - 20) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const color = score >= 85 ? "hsl(152 76% 32%)" : score >= 70 ? "hsl(158 64% 32%)" : score >= 55 ? "hsl(38 92% 45%)" : "hsl(354 78% 54%)";
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(214 32% 91%)" strokeWidth="10" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)} style={{ transition: "stroke-dashoffset 1s ease" }} />
      </svg>
      <div className="absolute text-center">
        <div className="font-mono text-4xl font-bold" style={{ color }}>{score}</div>
        <div className="text-[10px] text-muted-foreground">/ 100</div>
      </div>
    </div>
  );
}

export function StatCard({ label, value, sub, accent, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-border bg-card p-5 gc-ring-shadow">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-2 font-mono text-2xl font-bold" style={{ color: accent || "hsl(217 63% 15%)" }}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
